<template>
  <div class="page p-persona-step">
    <map-wizard :facets="['persona']">
      <template #navigation>
        <wizard-navigation
          :facets="['persona']"
          :get-route-link="getRouteLink"
          :progress-idx="mapConfig.wizardStepProgressIdx"
          :wizard-step="activeWizardStep"
          :wizard-step-list="activeWizardStepList"
          class="p-persona-step__navigation"
        />
      </template>
      <template #step>
        <wizard-step :facets="['persona']" :wizard-step="activeWizardStep">
          <template #title>
            <span v-if="page.pretext" class="p-persona-step__pre-text">
              {{ page.pretext }}
            </span>
            <h1>
              {{ page.title }}
            </h1>
          </template>
          <template #default>
            <component :is="activeWizardStep.component" :page="page" />
          </template>
          <template #outer>
            <div class="p-persona-step__content-img">
              <responsive-img
                :src="interstitialContent.image"
                :width="660"
                alt=""
                class="p-persona-step__content-img-el"
              />
            </div>
            <div class="p-persona-step__persona-navigation">
              <persona-navigation />
            </div>
            <n-link
              v-if="isLastStep"
              :to="localePath({ name: 'viewpoint' })"
              class="p-persona-step__back-btn btn btn--primary btn--small"
            >
              {{ $t('persona_wizard.back_btn') }}
            </n-link>
          </template>
        </wizard-step>
      </template>
      <template #map>
        <world-map
          :allow-rotation="activeWizardStep.allowRotation"
          :allow-zoom="activeWizardStep.allowZoom"
          :facets="[]"
          :map-padding="mapPadding"
          class="p-persona-step__map"
          v-bind="mapConfig"
          @map-drawn="onMapDrawn($event)"
        />
      </template>
    </map-wizard>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import MapWizard from '@/components/map-wizard/map-wizard'
  import { mapConfigIds } from '@/assets/js/model/map-config'
  import WizardNavigation from '@/components/wizard-navigation/wizard-navigation'
  import WizardStep from '@/components/wizard-step/wizard-step'
  import { transformKeysDeep } from '@/assets/js/util/misc/transform'
  import { getImage } from '@/assets/js/util/misc/image'
  import { interstitialContentList } from '@/assets/js/model/interstitial-content'
  import WorldMap from '@/components/world-map/world-map'
  import { overlayInstanceIds } from '@/assets/js/model/overlay-instance'
  import { pageIds } from '@/assets/js/model/page'
  import PersonaNavigation from '@/components/persona-navigation/persona-navigation'
  import { viewportWidthIds, viewportWidthList } from '@/assets/js/util/dom/viewport-width'
  import ResponsiveImg from '@/components/responsive-img/responsive-img'

  const mdViewport = viewportWidthList.find(({ id }) => id === viewportWidthIds.MD)

  export default {
    meta: {
      titleKey: 'meta.persona.title',
      pageId: pageIds.PERSONA_WIZARD,
    },
    validator({ params }) {
      return !!params.step
    },
    components: {
      ResponsiveImg,
      PersonaNavigation,
      WorldMap,
      WizardStep,
      WizardNavigation,
      MapWizard,
    },
    beforeRouteLeave(_, __, next) {
      this.closeOverlay({ id: overlayInstanceIds.MAP_STATIONS })
      this.setMapConfig({
        ...this.mapConfig,
        wizardStepProgressIdx: 0,
      })
      next()
    },
    async asyncData({ $content, app, params }) {
      let page

      try {
        const slug = `${params.persona}_${params.step}`
        page = await $content('personas', app.i18n.locale, slug).fetch()
      } catch (e) {
        // error({ statusCode: 404, message: 'Persona step content not found!' })
        page = {
          title: '404 - Page not found!',
          rich_text: 'Somebody forgot to create this page...',
        }
      }

      return {
        page: transformKeysDeep(page),
      }
    },
    computed: {
      ...mapState('overlay', ['overlays']),
      ...mapState('viewport', ['width']),
      ...mapState('world-map', ['mapConfigs']),
      ...mapGetters('navigation', [
        'activePersona',
        'activePageTheme',
        'activeWizardStep',
        'activeWizardStepList',
      ]),
      isLastStep() {
        return parseInt(this.$route.params.step, 10) === 6
      },
      mapPadding() {
        if (this.width < mdViewport.minValue) {
          return [10, 10, 10, 10]
        } else {
          return [40, 40, 40, 80 + 40]
        }
      },
      mapConfig() {
        return this.mapConfigs[mapConfigIds.PERSONA_WIZARD]
      },
      wizardStepProgressIdx() {
        return Math.max(
          this.mapConfig ? this.mapConfig.wizardStepProgressIdx : 0,
          this.interstitialContent.wizardStepIdx,
        )
      },
      interstitialContent() {
        const { id } = this.activeWizardStep
        return interstitialContentList.find(({ wizardStepId }) => wizardStepId === id)
      },
    },
    watch: {
      'activeWizardStep.id': {
        handler() {
          this.setMapConfig({
            ...this.activeWizardStep.mapConfig,
            wizardStepProgressIdx: this.wizardStepProgressIdx,
          })
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions('overlay', ['closeOverlay', 'openOverlay', 'updateOverlay']),
      ...mapActions('world-map', ['setMapConfig']),
      getRouteLink(param) {
        return this.localePath({
          name: 'persona-persona-step-step',
          params: { ...this.$route.params, step: param },
          query: this.$route.query,
        })
      },
      onMapDrawn({ stations }) {
        const labeledStations = stations.map((position, positionIdx) => {
          return {
            x: position.x,
            y: position.y,
            label: `data_model.persona.${this.activePersona.id}.stations[${positionIdx}]`,
          }
        })

        if (!this.overlays[overlayInstanceIds.MAP_STATIONS]) {
          this.openOverlay({
            id: overlayInstanceIds.MAP_STATIONS,
            transition: 'trs-simple-fade',
            disableScroll: false,
            facets: ['map-stations'],
            component: 'overlay-map-stations-content',
            props: { stations: labeledStations },
          })
        } else {
          this.updateOverlay({
            id: overlayInstanceIds.MAP_STATIONS,
            props: { stations: labeledStations },
          })
        }
      },
    },
    getImage,
  }
</script>

<style lang="scss" src="@/assets/scss/pages/_p-persona-step.scss" />
